/**
 * Siemens MLFB exact-match lookup.
 * Normalizes part numbers the same way as the backend (hyphenless lowercase key).
 */
(function (global) {
  'use strict';

  function normalizeMlfb(input) {
    if (input == null) return '';
    return String(input).replace(/[\s\-.]/g, '').toLowerCase();
  }

  function buildPartUrl(key, basePath) {
    var base = (basePath || '/pages/siemens-obsolete/').replace(/\/$/, '');
    return base + '/' + key;
  }

  function pageLooksLikeObsoletePart(html) {
    return (
      html.indexOf('section-siemens-obsolete') !== -1 ||
      html.indexOf('siem-obs__title') !== -1 ||
      html.indexOf('siem-obs-') !== -1
    );
  }

  async function checkPartExists(url) {
    var headRes = await fetch(url, {
      method: 'HEAD',
      credentials: 'same-origin',
      redirect: 'follow',
    });

    if (headRes.ok) return true;
    if (headRes.status !== 404 && headRes.status !== 405) return false;

    var getRes = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'follow',
    });

    if (!getRes.ok) return false;

    var html = await getRes.text();
    return pageLooksLikeObsoletePart(html);
  }

  async function resolveMlfb(raw, options) {
    var opts = options || {};
    var basePath = opts.basePath || '/pages/siemens-obsolete/';
    var key = normalizeMlfb(raw);

    if (!key) {
      return { status: 'invalid', raw: raw || '', key: '' };
    }

    var url = buildPartUrl(key, basePath);

    try {
      var exists = await checkPartExists(url);
      if (exists) {
        return { status: 'found', raw: raw, key: key, url: url };
      }
      return { status: 'miss', raw: raw, key: key, url: url };
    } catch (err) {
      return {
        status: 'error',
        raw: raw,
        key: key,
        url: url,
        error: err && err.message ? err.message : 'Lookup failed',
      };
    }
  }

  function recordSearchMiss(detail) {
    global.dispatchEvent(
      new CustomEvent('siemens-mlfb-miss', {
        detail: detail,
      })
    );
  }

  function initLookupRoot(root) {
    if (!root || root.dataset.siemensLookupReady === 'true') return;

    var form = root.querySelector('[data-siemens-lookup-form]');
    var input = root.querySelector('[data-siemens-lookup-input]');
    var submitBtn = root.querySelector('[data-siemens-lookup-submit]');
    var statusEl = root.querySelector('[data-siemens-lookup-status]');
    var errorEl = root.querySelector('[data-siemens-lookup-error]');
    var missEl = root.querySelector('[data-siemens-lookup-miss]');
    var missPartEl = root.querySelector('[data-siemens-lookup-miss-part]');
    var rfqBtn = root.querySelector('[data-siemens-lookup-rfq]');

    if (!form || !input || !submitBtn) return;

    var basePath = root.dataset.basePath || '/pages/siemens-obsolete/';
    var submitLabel = submitBtn.dataset.defaultLabel || submitBtn.textContent.trim();
    var missPageUrl = root.dataset.missPageUrl || '';
    var missRaw = '';

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      submitBtn.textContent = isLoading ? 'Looking up…' : submitLabel;
      input.disabled = isLoading;
      if (statusEl) statusEl.classList.toggle('is-visible', isLoading);
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.add('is-visible');
      if (missEl) missEl.classList.remove('is-visible');
    }

    function clearFeedback() {
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
      }
      if (missEl) missEl.classList.remove('is-visible');
    }

    async function runLookup() {
      var raw = input.value.trim();
      clearFeedback();

      if (!raw) {
        showError('Enter a Siemens part number to continue.');
        return;
      }

      setLoading(true);

      try {
        var result = await resolveMlfb(raw, { basePath: basePath });

        if (result.status === 'found') {
          window.location.assign(result.url);
          return;
        }

        if (result.status === 'invalid') {
          showError('Enter a valid Siemens part number to continue.');
          return;
        }

        if (result.status === 'error') {
          showError(result.error || 'Something went wrong. Please try again.');
          return;
        }

        if (missPageUrl) {
          var missUrl = missPageUrl + (missPageUrl.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(raw);
          window.location.assign(missUrl);
          return;
        }

        missRaw = raw;
        if (missPartEl) missPartEl.textContent = raw;
        if (missEl) missEl.classList.add('is-visible');

        recordSearchMiss({
          mlfb_raw: raw,
          mlfb_key: result.key,
          seen_at: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      runLookup();
    });

    if (rfqBtn) {
      rfqBtn.addEventListener('click', function () {
        global.dispatchEvent(
          new CustomEvent('open-quote-modal', {
            detail: {
              partNumber: missRaw,
              partName: 'Siemens part lookup — ' + missRaw,
              vendor: 'Siemens',
              quantity: 1,
              intent: 'search_miss',
              leadSource: 'siemens_obsolete_lookup',
            },
          })
        );
      });
    }

    root.dataset.siemensLookupReady = 'true';

    if (root.dataset.autoLookup === 'true' && input.value.trim()) {
      runLookup();
    }
  }

  function bootLookup() {
    var roots = document.querySelectorAll('[data-siemens-lookup-root]');
    for (var i = 0; i < roots.length; i++) {
      initLookupRoot(roots[i]);
    }
  }

  global.SiemensMlfbLookup = {
    normalizeMlfb: normalizeMlfb,
    buildPartUrl: buildPartUrl,
    resolveMlfb: resolveMlfb,
    recordSearchMiss: recordSearchMiss,
    initLookupRoot: initLookupRoot,
    bootLookup: bootLookup,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootLookup);
  } else {
    bootLookup();
  }
})(window);
